import { useState, useEffect, useCallback } from 'react'
import { openDB } from 'idb'
import api from '../utils/api'

const DB_NAME    = 'martyrform_offline'
const STORE_NAME = 'submission_queue'
const DB_VERSION = 1

async function getDB() {
  return openDB(DB_NAME, DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true })
        store.createIndex('status', 'status')
      }
    },
  })
}

const MAX_NON_VIDEO_BYTES = 20 * 1024 * 1024 // 20 MB

export function useOfflineQueue() {
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  const [queue, setQueue] = useState([])
  const [syncing, setSyncing] = useState(false)

  useEffect(() => {
    const handleOnline  = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)
    window.addEventListener('online',  handleOnline)
    window.addEventListener('offline', handleOffline)
    return () => {
      window.removeEventListener('online',  handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  const loadQueue = useCallback(async () => {
    try {
      const db = await getDB()
      const all = await db.getAll(STORE_NAME)
      setQueue(all)
    } catch (e) {
      console.warn('Failed to load offline queue:', e)
    }
  }, [])

  useEffect(() => {
    loadQueue()
  }, [loadQueue])

  // Auto-sync when back online
  useEffect(() => {
    if (isOnline) {
      syncQueue()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline])

  /**
   * Enqueue a submission for offline processing.
   * Files that are video (video/mp4, video/*) are excluded from the queue.
   * Total non-video file bytes must not exceed MAX_NON_VIDEO_BYTES.
   */
  const enqueue = useCallback(async (formData, files) => {
    try {
      const db = await getDB()

      // Serialize non-video files as ArrayBuffer
      const serializedFiles = {}
      let totalBytes = 0

      if (files.martyr_photo_upload) {
        const ab = await files.martyr_photo_upload.arrayBuffer()
        totalBytes += ab.byteLength
        if (totalBytes <= MAX_NON_VIDEO_BYTES) {
          serializedFiles.martyr_photo_upload = {
            name: files.martyr_photo_upload.name,
            type: files.martyr_photo_upload.type,
            data: ab,
          }
        }
      }

      serializedFiles.evidence_upload = []
      for (const f of files.evidence_upload || []) {
        if (f.type.startsWith('video/')) continue // videos not stored offline
        const ab = await f.arrayBuffer()
        totalBytes += ab.byteLength
        if (totalBytes <= MAX_NON_VIDEO_BYTES) {
          serializedFiles.evidence_upload.push({ name: f.name, type: f.type, data: ab })
        }
      }

      const item = {
        formData,
        files: serializedFiles,
        status: 'pending',
        createdAt: new Date().toISOString(),
        attempts: 0,
      }
      await db.add(STORE_NAME, item)
      await loadQueue()
      return true
    } catch (e) {
      console.warn('Enqueue failed:', e)
      return false
    }
  }, [loadQueue])

  const syncQueue = useCallback(async () => {
    if (syncing) return
    setSyncing(true)
    try {
      const db = await getDB()
      const pending = await db.getAllFromIndex(STORE_NAME, 'status', 'pending')
      for (const item of pending) {
        try {
          const fd = new FormData()
          fd.append('data', JSON.stringify(item.formData))

          if (item.files?.martyr_photo_upload) {
            const f = item.files.martyr_photo_upload
            fd.append('martyr_photo', new Blob([f.data], { type: f.type }), f.name)
          }
          for (const ef of item.files?.evidence_upload || []) {
            fd.append('evidence_files', new Blob([ef.data], { type: ef.type }), ef.name)
          }

          await api.post('/api/v1/submissions', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
          await db.delete(STORE_NAME, item.id)
        } catch (e) {
          await db.put(STORE_NAME, { ...item, attempts: item.attempts + 1, lastError: e.message })
        }
      }
      await loadQueue()
    } finally {
      setSyncing(false)
    }
  }, [syncing, loadQueue])

  const removeFromQueue = useCallback(async (id) => {
    const db = await getDB()
    await db.delete(STORE_NAME, id)
    await loadQueue()
  }, [loadQueue])

  const pendingCount = queue.filter((q) => q.status === 'pending').length

  return { isOnline, queue, pendingCount, syncing, enqueue, syncQueue, removeFromQueue }
}
